import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { NotificationPrefs, type NotificationLevel } from '@/lib/api';
import { useChatStore } from '@/stores/chat';

interface NotificationPrefsMenuProps {
  conversationId: string;
  open: boolean;
  onClose: () => void;
  // Anchor position to render near (the conversation row or a kebab button).
  anchorRect?: DOMRect | null;
}

interface Option {
  value: NotificationLevel;
  label: string;
  description: string;
}

const OPTIONS: Option[] = [
  { value: 'all', label: 'All messages', description: 'Get notified for every new message in this conversation.' },
  { value: 'mentions', label: 'Mentions only', description: 'Only notify me when someone @mentions me.' },
  { value: 'off', label: 'Off', description: 'No notifications from this conversation.' },
];

/**
 * Popover menu for the per-conversation notification level. Anchored to a
 * caller-provided `anchorRect`; falls back to a centered modal-like layout
 * when no anchor is supplied so it still works in tests / unusual parents.
 */
export default function NotificationPrefsMenu({
  conversationId,
  open,
  onClose,
  anchorRect,
}: NotificationPrefsMenuProps) {
  const [selected, setSelected] = useState<NotificationLevel>('all');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Seed the selection from the server when the popover opens. Falls back
  // to the in-memory store (populated by previous edits) to keep things
  // responsive.
  useEffect(() => {
    if (!open) return;
    const cached = useChatStore.getState().notificationPrefs.get(conversationId);
    if (cached) setSelected(cached);

    let cancelled = false;
    setLoading(true);
    setError(null);
    NotificationPrefs.get(conversationId)
      .then((r) => {
        if (cancelled) return;
        setSelected(r.level);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        const msg =
          e && typeof e === 'object' && 'message' in e
            ? String((e as { message: unknown }).message)
            : 'Could not load preference';
        setError(msg);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, conversationId]);

  // Close on outside click + Esc.
  useEffect(() => {
    if (!open) return;
    function onPointer(e: PointerEvent) {
      const el = containerRef.current;
      if (!el) return;
      if (e.target instanceof Node && !el.contains(e.target)) {
        onClose();
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  // Anchor positioning: derive a style from the rect when provided,
  // otherwise fall back to a centered dialog.
  const style: React.CSSProperties | undefined = anchorRect
    ? {
        position: 'absolute',
        top: Math.max(8, anchorRect.bottom + 6),
        left: Math.max(8, Math.min(window.innerWidth - 280, anchorRect.right - 264)),
      }
    : undefined;

  const wrapperClass = anchorRect
    ? 'fixed inset-0 z-40'
    : 'fixed inset-0 z-40 flex items-center justify-center bg-black/30 p-4';

  return (
    <div
      role="presentation"
      className={wrapperClass}
      onClick={(e) => {
        // Click on the dim layer closes; clicks on the panel itself don't.
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={containerRef}
        role="dialog"
        aria-modal="true"
        aria-label="Notification preferences"
        className={clsx(
          'w-64 overflow-hidden rounded-xl border border-ink-100 bg-white shadow-soft',
          'dark:border-ink-700 dark:bg-ink-800',
        )}
        style={style}
      >
        <div className="border-b border-ink-100 px-3 py-2 text-sm font-medium dark:border-ink-700">
          Notifications
        </div>

        <fieldset disabled={loading || saving} className={clsx('p-2', (loading || saving) && 'opacity-60')}>
          <legend className="sr-only">Notification level</legend>
          {OPTIONS.map((opt) => {
            const id = `np-${conversationId}-${opt.value}`;
            return (
              <label
                key={opt.value}
                htmlFor={id}
                className={clsx(
                  'flex cursor-pointer items-start gap-2 rounded-md px-2 py-1.5 text-sm',
                  'hover:bg-ink-100 dark:hover:bg-ink-700',
                )}
              >
                <input
                  id={id}
                  type="radio"
                  name={`notification-pref-${conversationId}`}
                  value={opt.value}
                  checked={selected === opt.value}
                  onChange={() => handlePick(opt.value)}
                  className="mt-0.5 h-4 w-4 accent-brand-500"
                />
                <span className="min-w-0 flex-1">
                  <span className="block font-medium">{opt.label}</span>
                  <span className="block text-xs text-ink-500 dark:text-ink-300">{opt.description}</span>
                </span>
              </label>
            );
          })}
        </fieldset>

        {error && (
          <div className="border-t border-ink-100 px-3 py-2 text-xs text-red-600 dark:border-ink-700 dark:text-red-400">
            {error}
          </div>
        )}
      </div>
    </div>
  );

  async function handlePick(level: NotificationLevel) {
    // F-006: optimistic update with rollback. Capture the previous value
    // BEFORE we mutate the store / radio so we can restore both on
    // failure. Without the rollback, a transient network error leaves
    // the bell icon stuck at the wrong level until the next menu open.
    const prev = useChatStore.getState().getConversationPref(conversationId);
    if (prev === level) {
      // No-op — nothing to save.
      return;
    }
    setSelected(level);
    setSaving(true);
    setError(null);
    useChatStore.getState().setNotificationPref(conversationId, level);
    try {
      await NotificationPrefs.set(conversationId, level);
    } catch (e: unknown) {
      // Rollback: restore both the store and the local radio state so the
      // UI matches the persisted server value.
      useChatStore.getState().setNotificationPref(conversationId, prev);
      setSelected(prev);
      const msg =
        e && typeof e === 'object' && 'message' in e
          ? String((e as { message: unknown }).message)
          : 'Could not save preference';
      setError(msg);
    } finally {
      setSaving(false);
    }
  }
}